import React, {useEffect, useState} from 'react'
import {Trans, useTranslation} from 'react-i18next'

import StatusMessage, {StatusEnum} from "../../share/StatusMessage"
import {FormProvider, useForm} from "react-hook-form"
import {
    ContextualMenuItemType,
    DefaultButton,
    DetailsListLayoutMode,
    FontWeights,
    IButtonStyles,
    IColumn,
    IconButton,
    IContextualMenuProps,
    Label,
    PrimaryButton,
    SelectionMode,
    Separator,
    Shimmer,
    ShimmeredDetailsList,
    ShimmerElementType,
    Spinner,
    SpinnerSize,
    Stack,
    StackItem,
    Text
} from "@fluentui/react"
import ApprovalService, {
    IDeleteApprovalRequest,
    IGetApprovalStepRequest,
    IGetApprovalStepResponse,
    IPostApprovalRequest, IPostApprovalResponse,
    IPutApprovalRequest,
    IPutApprovalStepRequest
} from "../../services/Approval"
import {onFormatDate} from "../../Helpers/DateUtils"
import {RHFTextBoxControl} from "../RHFControls/RHFTextBoxControl"
import {RHFCheckBoxControl} from "../RHFControls/RHFChekBoxControl"
import CustomDialog from "../../share/CustomDialog"
import {ApprovalStepEdit} from "./ApprovalStepEdit"
import {decode} from 'html-entities'


type Properties = {
    cancel: any
    templateId: string
    setSelectedPivot: any
    currentItem: IGetApprovalStepResponse | null
    setCurrentItem: any
    steps: IGetApprovalStepResponse[]
    setSteps: any
}

export function ApprovalEdit({
                                 cancel,
                                 currentItem,
                                 setCurrentItem,
                                 setSelectedPivot,
                                 setSteps,
                                 steps,
                                 templateId
                             }: Properties) {

    const {t} = useTranslation(['approval', 'common'])

    const form = useForm();

    const iconListButtonStyles: Partial<IButtonStyles> = {root: {float: 'left', height: 'inherit'}};

    const [approval, setApproval] = useState<IPostApprovalResponse | null>(null)
    const [approvalId, setApprovalId] = useState<number | null>(null)
    const [hasTimerMessageApprove, setHasTimerMessageApprove] = useState(false)
    const [isEmptyRecord, setIsEmptyRecord] = useState(true)
    const [isLoadedContent, setIsLoadedContent] = useState(false)
    const [isLoadingApprove, setIsLoadingApprove] = useState(true)
    const [isLoadingApproveSteps, setIsLoadingApproveSteps] = useState(true)
    const [isSaving, setIsSaving] = useState(false)
    const [isDeleting, setIsDeleting] = useState(false)
    const [messageApprove, setMessageApprove] = useState("")
    const [showApprovalStepModal, setShowApprovalStepModal] = useState(false)
    const [showDelete, setShowDelete] = useState(false)
    const [showDeleteStep, setShowDeleteStep] = useState(false)
    const [showMessageApprove, setShowMessageApprove] = useState(false)
    const [statusMessageApprove, setStatusMessageApprove] = useState<StatusEnum>(StatusEnum.Info)

    const menuApprovalStepsProps = (item: IGetApprovalStepResponse): IContextualMenuProps => {

        return {
            items: [
                {
                    key: 'approvalActions',
                    text: t("title.approvalActions", {ns: "approval"}),
                    title: t("title.approvalActions", {ns: "approval"}),
                    iconProps: {iconName: 'SetAction'},
                    onClick: () => {
                        EditStep(item)
                    }
                },
                {
                    key: 'edit',
                    text: t("title.editStepName", {ns: "approval"}),
                    title: t("title.editStepName", {ns: "approval"}),
                    iconProps: {iconName: 'Edit'},
                    onClick: () => {
                        setCurrentItem(item)
                        setShowApprovalStepModal(true)
                    }
                },
                {
                    key: 'Divider1',
                    itemType: ContextualMenuItemType.Divider
                },
                {
                    key: 'moveUp',
                    text: t("Common.MenuAction.Up", {ns: "common"}),
                    title: t("Common.MenuAction.Up", {ns: "common"}),
                    iconProps: {iconName: 'Up'},
                    disabled: steps.length > 0 && steps[0].Id === item.Id,
                    onClick: () => {
                        moveUpDown(item, -1).then()
                    }
                },
                {
                    key: 'moveDown',
                    text: t("Common.MenuAction.Down", {ns: "common"}),
                    title: t("Common.MenuAction.Down", {ns: "common"}),
                    iconProps: {iconName: 'Down'},
                    disabled: steps.length > 0 && steps[steps.length - 1].Id === item.Id,
                    onClick: () => {
                        moveUpDown(item, 1).then()
                    }
                },
                {
                    key: 'Divider2',
                    itemType: ContextualMenuItemType.Divider
                },
                {
                    key: 'delete',
                    text: t("Common.MenuAction.Delete", {ns: "common"}),
                    title: t("Common.MenuAction.Delete", {ns: "common"}),
                    iconProps: {iconName: 'Trash'},
                    onClick: () => {
                        setCurrentItem(item)
                        setShowDeleteStep(true)
                    }
                }

            ],
            directionalHintFixed: true,
        }
    }

    const approvalStepColumns: IColumn[] = [
        {
            key: 'column1',
            name: t('title.order', {ns: 'approval'}),
            fieldName: 'Order',
            minWidth: 40,
            maxWidth: 40,
            isMultiline: false,
            isResizable: false,
            onRenderHeader: () => {
                return <>
                    <Text variant={'medium'} title={t('title.order', {ns: 'approval'})}
                          styles={{root: {fontWeight: FontWeights.semibold}}}>
                        {t('title.order', {ns: 'approval'})}
                    </Text>
                </>
            },
        },
        {
            key: 'column2',
            name: t('title.name', {ns: 'approval'}),
            fieldName: 'Name',
            minWidth: 200,
            isMultiline: false,
            isResizable: true,
            onRenderHeader: () => {
                return <>
                    <Text variant={'medium'} title={t('title.name', {ns: 'approval'})}
                          styles={{root: {fontWeight: FontWeights.semibold}}}>
                        {t('title.name', {ns: 'approval'})}
                    </Text>
                </>
            },
        },
        {
            key: 'column3',
            name: t('title.dateCreated', {ns: 'approval'}),
            fieldName: 'DateCreated',
            minWidth: 100,
            maxWidth: 200,
            isMultiline: false,
            isResizable: true,
            isCollapsible: true,
            onRenderHeader: () => {
                return <>
                    <Text variant={'medium'} title={t('title.dateCreated', {ns: 'approval'})}
                          styles={{root: {fontWeight: FontWeights.semibold}}}>
                        {t('title.dateCreated', {ns: 'approval'})}
                    </Text>
                </>
            },
            onRender: (item: IGetApprovalStepResponse) => {
                return <>
                    <StackItem styles={{
                        root: {
                            justifyContent: "start",
                            alignItems: "center",
                            display: "flex",
                            height: "100%"
                        }
                    }}>
                        <Text block variant={"smallPlus"}
                              title={onFormatDate(new Date(item.DateCreated), "lll")}>{onFormatDate(new Date(item.DateCreated), "ll", true)}</Text>
                    </StackItem>
                </>
            }
        },
        {
            key: 'column4',
            name: t('title.dateUpdated', {ns: 'approval'}),
            fieldName: 'DateUpdated',
            minWidth: 100,
            maxWidth: 200,
            isMultiline: false,
            isResizable: true,
            isCollapsible: true,
            onRenderHeader: () => {
                return <>
                    <Text variant={'medium'} title={t('title.dateUpdated', {ns: 'approval'})}
                          styles={{root: {fontWeight: FontWeights.semibold}}}>
                        {t('title.dateUpdated', {ns: 'approval'})}
                    </Text>
                </>
            },
            onRender: (item: IGetApprovalStepResponse) => {
                return <>
                    <StackItem styles={{
                        root: {
                            justifyContent: "start",
                            alignItems: "center",
                            display: "flex",
                            height: "100%"
                        }
                    }}>
                        <Text block variant={"smallPlus"}
                              title={onFormatDate(new Date(item.DateUpdated), "lll")}>{onFormatDate(new Date(item.DateUpdated), "ll", true)}</Text>
                    </StackItem>
                </>
            }
        },
        {
            key: 'column5',
            name: t('title.actions', {ns: 'approval'}),
            minWidth: 50,
            maxWidth: 50,
            isMultiline: false,
            isResizable: false,
            onRenderHeader: () => {
                return <>
                    <Text variant={'medium'} title={t('title.actions', {ns: 'approval'})}
                          styles={{root: {fontWeight: FontWeights.semibold}}}>
                        {t('title.actions', {ns: 'approval'})}
                    </Text>
                </>
            },
            onRender: (item: IGetApprovalStepResponse) => {
                return <>
                    <StackItem styles={{
                        root: {
                            justifyContent: "center",
                            alignItems: "center",
                            display: "flex",
                            height: "100%"
                        }
                    }}>

                        <IconButton
                            menuIconProps={{iconName: 'MoreVertical', style: {fontSize: 20, color: '#242424'}}}
                            role="button"
                            title={t('label.colShowActions', {ns: 'common'})}
                            data={item}
                            disabled={isSaving || isLoadingApproveSteps || isDeleting}
                            aria-haspopup
                            aria-label={t('label.colShowActions', {ns: 'common'})}
                            styles={iconListButtonStyles}
                            menuProps={menuApprovalStepsProps(item)}
                        />
                    </StackItem>
                </>

            }
        }
    ]


    /**
     * Asynchronously moves an approval step up or down in the order based on the specified position.
     *
     * This function updates the approval step's order by a relative position and sends the updated data to the server.
     * If successful, it reloads the approval steps. In case of failure, it displays an error message.
     * During the process, a saving state is set to indicate ongoing activity.
     *
     * @param {IGetApprovalStepResponse} item - The approval step to be moved. Must include details such as approval ID, name, current order, template ID, and step ID.
     * @param {number} position - The relative position change for the approval step's order. Positive for moving up, negative for moving down.
     * @returns {Promise<void>} A promise that resolves after the operation is completed, either with successful execution or failure handling.
     */
    const moveUpDown = async (item: IGetApprovalStepResponse, position: number): Promise<void> => {

        setIsSaving(true)

        const requestKIM: IPutApprovalStepRequest = {
            ApprovalId: item.ApprovalId.toString(),
            Name: item.Name,
            TemplateId: templateId,
            Order: item.Order + position,
            Id: item.Id.toString()
        }

        await ApprovalService.putApprovalStep(requestKIM)
            .then(data => {
                if (data.Success) {
                    loadApproveSteps().then()
                } else {
                    ShowMessageApprove(t('message.error', {
                        ns: 'common',
                        Error: data.ExceptionMessage
                    }), StatusEnum.Error)
                }
            })
            .catch((error) => {
                ShowMessageApprove(t('message.error', {ns: 'common', Error: error}), StatusEnum.Error)
            })
            .finally(() => {
                setIsSaving(false)
            })
    }

    /**
     * Asynchronously loads and processes approval steps for a given template.
     */
    const loadApproveSteps = async () => {

        const request: IGetApprovalStepRequest = {
            TemplateId: templateId,
        }

        await ApprovalService.getApprovalStep(request)
            .then(data => {
                try {
                    const resultData = JSON.parse(data.Result) as Array<IGetApprovalStepResponse>

                    if (Array.isArray(resultData)) {
                        resultData.sort((a, b) => a.Order - b.Order);
                        setSteps(resultData)
                    } else {
                        ShowMessageApprove(t('message.notFound', {ns: 'common'}), StatusEnum.Error)
                    }
                } catch (error) {
                    ShowMessageApprove(t('message.error', {ns: 'common', Error: error.toString()}), StatusEnum.Error)
                }

            })
            .catch((error) => {
                ShowMessageApprove(t('message.error', {ns: 'common', Error: error}), StatusEnum.Error)
            })
            .finally(() => {
                setIsLoadingApproveSteps(false);
            })
    }

    /**
     * Updates the current editing step by setting the provided step data and switching the context to 'steps'.
     *
     * @param {IGetApprovalStepResponse} item - The step data to be edited, which includes information about the approval step.
     * @return {void} This function does not return a value.
     */
    function EditStep(item: IGetApprovalStepResponse): void {
        setCurrentItem(item)
        setSelectedPivot('steps')
    }

    /**
     * Load Template Context
     * @param clearMessages
     */
    const loadApprove = async (clearMessages: boolean = false) => {

        if (clearMessages) {
            ClearMessageApprove()
        }

        setIsLoadedContent(false)
        setIsLoadingApprove(true)
        setIsEmptyRecord(true)

        const request: IPostApprovalRequest = {
            Active: false,
            Description: '',
            Name: '',
            TemplateId: templateId,
        }

        await ApprovalService.postApproval(request)
            .then(data => {
                try {
                    const resultData = JSON.parse(data.Result) as Array<IPostApprovalResponse>

                    if (Array.isArray(resultData) && resultData.length > 0) {


                        if (resultData[0].Name.trim().length > 0 && resultData[0].Description.trim().length > 0) {
                            setIsEmptyRecord(false)
                        }

                        form.setValue('Name', resultData[0].Name)
                        form.setValue('Description', resultData[0].Description)
                        form.setValue('Active', resultData[0].Active)
                        form.setValue('Id', resultData[0].Id)

                        setApprovalId(resultData[0].Id)
                        setApproval(resultData[0])

                        setIsLoadedContent(true)
                        loadApproveSteps().then()

                    } else {
                        ShowMessageApprove(t('message.notFound', {ns: 'common'}), StatusEnum.Error)
                    }
                } catch (error) {
                    ShowMessageApprove(t('message.error', {ns: 'common', Error: error.toString()}), StatusEnum.Error)
                }

            })
            .catch((error) => {
                ShowMessageApprove(t('message.error', {ns: 'common', Error: error}), StatusEnum.Error)
            })
            .finally(() => {
                setIsLoadingApprove(false);
            })
    }


    useEffect(() => {
        loadApprove(true).then()
    }, [])


    /**
     * Deletes an approval step associated with the current item.
     */
    const deleteApprovalStep = async () => {

        ClearMessageApprove()
        setIsDeleting(true)
        setShowDeleteStep(false)

        const requestKim: IDeleteApprovalRequest = {
            TemplateId: templateId,
            Id: currentItem?.Id.toString() ?? '0'
        }

        await ApprovalService.deleteApprovalStep(requestKim)
            .then(data => {

                if (data.Success) {
                    ShowMessageApprove(t('message.successRemoved', {ns: 'common'}), StatusEnum.Success, true)
                    loadApproveSteps().then()
                } else {
                    ShowMessageApprove(t('message.error', {
                        ns: 'common',
                        Error: data.ExceptionMessage
                    }), StatusEnum.Error)
                }
            })
            .catch(error => {
                ShowMessageApprove(t('message.error', {ns: 'common', Error: error}), StatusEnum.Error)
            })
            .finally(() => {
                setShowDelete(false)
                setIsDeleting(false)
            })
    }

    /**
     * Delete
     */
    const deleteApprovalDefinition = async () => {
        ClearMessageApprove()
        setIsSaving(true)
        const requestKim: IDeleteApprovalRequest = {
            Id: form.getValues("Id"),
            TemplateId: templateId
        }
        await ApprovalService.deleteApproval(requestKim)
            .then(data => {
                if (data.Success) {
                    ShowMessageApprove(t('message.successRemoved', {ns: 'common'}), StatusEnum.Success, true)
                    loadApprove(false).then()
                } else {
                    ShowMessageApprove(t('message.error', {
                        ns: 'common',
                        Error: data.ExceptionMessage
                    }), StatusEnum.Error)
                }
            })
            .catch(error => {
                ShowMessageApprove(t('message.error', {ns: 'common', Error: error}), StatusEnum.Error)
            })
            .finally(() => {
                setShowDelete(false)
                setIsSaving(false)
            })
    }

    /**
     * Handles the update operation for managing approval data.
     *
     * This asynchronous function clears the form errors, prepares the data for an
     * approval update request, and submits the data using the ApprovalService. Once
     * the operation completes, it provides user feedback based on the result status
     * (success or error). Additionally, it manages a saving state during the update process.
     *
     * @function update
     * @async
     *
     * @returns {Promise<void>} A promise that resolves when the update process is completed.
     */

    const update = async (): Promise<void> => {
        form.clearErrors()
        ClearMessageApprove()
        await form.handleSubmit(async () => {

                setIsSaving(true)

                const requestKim: IPutApprovalRequest = {
                    Id: form.getValues("Id"),
                    TemplateId: templateId,
                    Description: form.getValues("Description"),
                    Name: form.getValues("Name"),
                    Active: form.getValues("Active"),
                }

                await ApprovalService.putApproval(requestKim)
                    .then(data => {
                        if (data.Success) {
                            ShowMessageApprove(t('message.successCreated', {ns: 'common'}), StatusEnum.Success, true)
                            setIsEmptyRecord(false)
                        } else {

                            form.setValue('Active', approval!.Active)

                            ShowMessageApprove(t('message.error', {
                                ns: 'common',
                                Error: data.ExceptionMessage
                            }), StatusEnum.Error)
                        }
                    })
                    .catch(error => {
                        form.setValue('Active', approval!.Active)
                        ShowMessageApprove(t('message.error', {ns: 'common', Error: error}), StatusEnum.Error)
                    })
                    .finally(() => {
                        setIsSaving(false)
                    })

            }, () => {

            }
        )();
    }

    /**
     * Represents a configuration array for a shimmer effect layout with elements in the first row.
     * Each item in the array defines a specific shimmer element type (e.g., line or gap) along with
     * its respective height and width properties.
     */

    const shimmerWithElementFirstRow = [
        {type: ShimmerElementType.line, height: 16, width: '35%'},
        {type: ShimmerElementType.gap, width: '2%'},
        {type: ShimmerElementType.line, height: 16, width: '35%'},
        {type: ShimmerElementType.gap, width: '2%'},
        {type: ShimmerElementType.line, height: 16, width: '30%'},
    ]

    /**
     * Displays an approval message with a specified status.
     *
     * @param {string} message - The message content to be displayed.
     * @param {StatusEnum} status - The status associated with the approval message.
     * @param hasTimer
     * @return {void} This function does not return a value.
     */

    function ShowMessageApprove(message: string, status: StatusEnum, hasTimer: boolean = false): void {
        setMessageApprove(decode(message))
        setStatusMessageApprove(status)
        setHasTimerMessageApprove(hasTimer)
        setShowMessageApprove(true)
    }

    /**
     * Clears the message approval status by resetting the message content and hiding the approval notification.
     *
     * @return {void} This function does not return any value.
     */

    function ClearMessageApprove(): void {
        setMessageApprove('')
        setShowMessageApprove(false)
        setHasTimerMessageApprove(false)
    }

    /**
     * Configuration object for contextual menu properties.
     */

    const menuProps: IContextualMenuProps = {
        items: [
            {
                key: 'deleteTemplateDefinition',
                text: t('Common.Button.Remove', {ns: 'common'}),
                iconProps: {iconName: 'Trash'},
                onClick: () => {
                    setShowDelete(true)
                }
            },
        ],
    }

    /**
     * Callback function to handle the closure of the approval step modal.
     *
     * @param {boolean} [update=false] - A flag indicating whether an update action should be triggered
     *                                   upon closing the modal. Defaults to false.
     */

    const onCloseApprovalStepModal = (update: boolean = false) => {
        setShowApprovalStepModal(false)

        if (update) {
            loadApproveSteps().then()
        }
    }

    /**
     * Opens a modal to allow adding a new approval step by resetting the current item
     * and displaying the approval step modal.
     *
     * @return {void} No return value.
     */

    function AddNewStep(): void {
        setCurrentItem(null)
        setShowApprovalStepModal(true)
    }

    return (
        <>

            <Stack grow={1} styles={{root: {overflowY: 'auto', paddingLeft: 16, paddingRight: 16}}}
                   className="scrollVisible">


                {showMessageApprove &&
                    <>
                        <Stack styles={{root: {paddingTop: 8}}}>
                            <StatusMessage status={statusMessageApprove} hasTimer={hasTimerMessageApprove}
                                           truncated={true}
                                           isMultiline={true}
                                           dismiss={true}

                                           setShowMessage={setShowMessageApprove}>
                                <div>{messageApprove}</div>
                            </StatusMessage>
                        </Stack>
                    </>
                }

                <label className="ms-fontSize-18">
                    {t('title.section', {ns: 'approval'})}
                </label>


                <Text variant={'medium'} styles={{root: {paddingTop: 8, paddingBottom: 8}}}>
                    <Trans>
                        {t('text.instruction', {ns: 'approval'})}
                    </Trans>
                </Text>

                <FormProvider {...form}>
                    <form>

                        {isLoadingApprove && <>
                            <Shimmer styles={{root: {marginTop: 16}}}
                                     shimmerElements={shimmerWithElementFirstRow}/>
                        </>
                        }

                        {!isLoadingApprove &&
                            <>
                                <Stack horizontal tokens={{childrenGap: 8}} wrap={true}
                                       style={{overflow: "hidden"}}>

                                    <StackItem grow={1}>
                                        <RHFTextBoxControl
                                            id={"Name"}
                                            label={t('label.ctrlName', {ns: 'common'})}

                                            rules={{
                                                required: t('text.fieldRequired', {ns: 'common'}),
                                                validate: {
                                                    required:
                                                        (value) => {
                                                            return !!value.trim() || t('text.fieldRequired', {ns: 'common'})
                                                        }
                                                },
                                            }}

                                            placeholder={t('label.ctrlName', {ns: 'common'})}
                                            title={t('label.ctrlName', {ns: 'common'})}
                                            ariaLabel={t('label.ctrlName', {ns: 'common'})}
                                            maxLength={20}
                                            control={form.control}
                                            setValue={form.setValue}
                                            getValues={form.getValues}
                                            trigger={form.trigger}
                                            disabled={!isLoadedContent || isSaving || isDeleting}
                                            required
                                        >
                                        </RHFTextBoxControl>

                                    </StackItem>
                                    <StackItem grow={1}>
                                        <RHFTextBoxControl
                                            id={"Description"}
                                            label={t('label.ctrlDescription', {ns: 'common'})}
                                            rules={{
                                                required: t('text.fieldRequired', {ns: 'common'}),
                                                validate: {
                                                    required:
                                                        (value) => {
                                                            return !!value.trim() || t('text.fieldRequired', {ns: 'common'})
                                                        }
                                                },
                                            }}
                                            placeholder={t('label.ctrlDescription', {ns: 'common'})}
                                            title={t('label.ctrlDescription', {ns: 'common'})}
                                            ariaLabel={t('label.ctrlDescription', {ns: 'common'})}
                                            maxLength={250}
                                            control={form.control}
                                            setValue={form.setValue}
                                            getValues={form.getValues}
                                            trigger={form.trigger}
                                            disabled={!isLoadedContent || isSaving || isDeleting}
                                            required
                                        >
                                        </RHFTextBoxControl>

                                    </StackItem>
                                    <StackItem grow={1}>
                                        <Label>{t('label.status', {ns: 'common'})}</Label>
                                        <RHFCheckBoxControl
                                            id={"Active"}
                                            label={`${t('label.active', {ns: 'common'})}/${t('label.inactive', {ns: 'common'})}`}
                                            title={t('label.status', {ns: 'common'})}
                                            ariaLabel={t('label.status', {ns: 'common'})}
                                            control={form.control}
                                            setValue={form.setValue}
                                            getValues={form.getValues}
                                            trigger={form.trigger}

                                            disabled={!isLoadedContent || isSaving || isDeleting}
                                        >
                                        </RHFCheckBoxControl>

                                    </StackItem>
                                </Stack>
                            </>
                        }
                    </form>
                </FormProvider>
                <Separator styles={{root: {paddingTop: 8}}}/>

                <Stack horizontal styles={{root: {alignItems: "center"}}}>
                    <StackItem grow={1}>
                        <Text variant={'mediumPlus'}
                              styles={{root: {fontWeight: FontWeights.semibold}}}>
                            {t('title.approvalSteps', {ns: 'approval'})}
                        </Text>
                    </StackItem>
                    <StackItem>
                        <DefaultButton
                            title={t('text.addStep', {ns: 'approval'})}
                            text={t('text.addStep', {ns: 'approval'})}
                            disabled={!isLoadedContent || isSaving || isLoadingApproveSteps || isDeleting || isEmptyRecord}
                            onClick={AddNewStep}
                        />
                    </StackItem>
                </Stack>

                <ShimmeredDetailsList
                    items={steps}
                    enableShimmer={isLoadingApproveSteps}
                    compact={false}
                    columns={approvalStepColumns}
                    selectionMode={SelectionMode.none}
                    layoutMode={DetailsListLayoutMode.justified}
                    isHeaderVisible={true}
                    isSelectedOnFocus={false}
                    setKey={"Id"}
                />

                {steps.length === 0 && !isLoadingApproveSteps &&
                    <>
                        <p style={{textAlign: "center"}}>
                            <Text variant={'small'}>
                                <Trans>
                                    {t('text.empty', {ns: 'approval'})}
                                </Trans>
                            </Text>
                        </p>
                    </>
                }

            </Stack>


            {/*-- START FOOTER --*/}
            <Separator></Separator>
            <Stack horizontal tokens={{childrenGap: 8}}
                   styles={{root: {paddingRight: 16, paddingBottom: 12, alignItems: 'center'}}}>
                <Stack grow={1}>&nbsp;</Stack>
                {isLoadingApprove &&
                    <>
                        <StackItem>
                            <Spinner size={SpinnerSize.medium} labelPosition="left"
                                     label={t('text.spinner', {ns: 'common'})}/>
                        </StackItem>
                    </>
                }
                {isSaving &&
                    <>
                        <StackItem>
                            <Spinner size={SpinnerSize.medium} labelPosition="left"
                                     label={t('text.spinnerSaving', {ns: 'common'})}/>
                        </StackItem>
                    </>
                }

                {isDeleting &&
                    <>
                        <StackItem>
                            <Spinner size={SpinnerSize.medium} labelPosition="left"
                                     label={t('text.spinnerDeleting', {ns: 'common'})}/>
                        </StackItem>
                    </>
                }

                <Stack horizontal tokens={{childrenGap: 8}} wrap={false}>


                    <PrimaryButton
                        disabled={!isLoadedContent || isSaving || isLoadingApproveSteps || isDeleting}
                        onClick={update}
                        split
                        menuProps={menuProps}
                        text={t('Common.Button.Save', {ns: 'common'})}
                        title={t('Common.Button.Save', {ns: 'common'})}/>


                    <DefaultButton onClick={cancel}
                                   text={t('Common.Button.Close', {ns: 'common'})}
                                   title={t('Common.Button.Close', {ns: 'common'})}/>

                </Stack>
            </Stack>


            <CustomDialog title={t('Common.Button.Remove', {ns: 'common'})} titleId={`remove${templateId}`}
                          actionText={t('Common.Button.Remove', {ns: 'common'})}
                          confirm={deleteApprovalDefinition}
                          close={() => {
                              setShowDelete(false)
                          }}
                          isOpen={showDelete}
            >
                <Trans>
                    {t('text.confirmRemove', {ns: 'approval'})}
                </Trans>
            </CustomDialog>

            <CustomDialog title={t('title.deleteStep', {ns: 'approval', Name: currentItem?.Name ?? ''})}
                          titleId={`removeStep${templateId}`}
                          actionText={t('Common.Button.Delete', {ns: 'common'})}
                          confirm={deleteApprovalStep}
                          close={() => {
                              setShowDeleteStep(false)
                          }}
                          isOpen={showDeleteStep}
            >
                <Trans>
                    {t('text.confirmRemoveStep', {ns: 'approval'})}
                </Trans>
            </CustomDialog>


            {showApprovalStepModal &&
                <ApprovalStepEdit approvalId={approvalId} templateId={templateId} item={currentItem}
                                  onDismissModal={onCloseApprovalStepModal}
                                  showModal={showApprovalStepModal}/>
            }


        </>
    )
}
